import * as React from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Box, 
  Toolbar, 
  Typography, 
  Container, 
  Button,
  IconButton,
  Drawer,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  Avatar,
  ListItemButton,
  List,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AirIcon from '@mui/icons-material/Air';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionIcon from '@mui/icons-material/Description';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadIcon from '@mui/icons-material/Upload';
import StarIcon from '@mui/icons-material/Star';
import CloudUpload from '@mui/icons-material/CloudUpload';
import {
  AirHeatExchangerIcon,
  ColdPlateIcon,
  CoaxialIcon,
  ShellAndTubeDryIcon,
  ShellAndTubeIcon,
  TubeComparisonIcon,
} from '../../common/icons';
import { useAuth } from '../../context/AuthContext';
import { AppAccess, IconType } from '../../types/appAccess';
import { getFullUrl } from '../../config/env';
import SettingsDialog from './SettingsDialog';
import FileUploadButton from '../common/FileUploadButton';
import SupportChatBox from '../common/SupportChatBox';
import ThemeToggle from '../common/ThemeToggle';

interface UserDisplayInfo {
  username?: string;
  first_name?: string;
  last_name?: string;
}

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

interface DrawerItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  requiredPermission?: string;
}

const Layout: React.FC<LayoutProps> = ({ children, title }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, appAccess } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [currentFormValues, setCurrentFormValues] = React.useState<any>(null);
  const [currentResults, setCurrentResults] = React.useState<any[]>([]);
  const [currentFormName, setCurrentFormName] = React.useState<string>('');
  const fileUploadButtonRef = React.useRef<any>(null);
  
  // HTRI import state
  const [htriFileInputRef] = React.useState(React.createRef<HTMLInputElement>());
  const [isHTRIParsing, setIsHTRIParsing] = React.useState(false);
  const [htriParseError, setHtriParseError] = React.useState<string | null>(null);
  const [htriDialogOpen, setHtriDialogOpen] = React.useState(false);
  const [htriExtractedValues, setHtriExtractedValues] = React.useState<any>(null);
  const [htriFilename, setHtriFilename] = React.useState<string | null>(null);

  // Check if we're in an app view - used to hide the footer
  // Use dynamic check based on user's accessible apps instead of hardcoded paths
  const isAppView = appAccess?.accessible_apps.some(app => 
    location.pathname === app.app_path
  ) ?? false;

  // Listen for form value changes from the current app
  React.useEffect(() => {
    const handleFormValuesChange = (event: CustomEvent) => {
      if (event.detail) {
        setCurrentFormValues(event.detail.formValues || null);
        setCurrentResults(event.detail.results || []);
        setCurrentFormName(event.detail.formName || '');
      } else {
        // Clear context when event.detail is null
        setCurrentFormValues(null);
        setCurrentResults([]);
        setCurrentFormName('');
      }
    };

    window.addEventListener('formValuesChange' as any, handleFormValuesChange);
    return () => {
      window.removeEventListener('formValuesChange' as any, handleFormValuesChange);
    };
  }, []);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleUploadClick = () => {
    // Trigger the FileUploadButton's click handler
    if (fileUploadButtonRef.current) {
      const button = fileUploadButtonRef.current.querySelector('button');
      if (button) {
        button.click();
      }
    }
  };

  // HTRI import handlers
  const handleHTRIFileChange = React.useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsHTRIParsing(true);
      setHtriParseError(null);
      setHtriFilename(file.name);
      
      try {
        // Import the HTRI service dynamically
        const { default: fileImportService } = await import('../../services/fileImportService');
        const values = await fileImportService.parseHTRIFile(file);
        setHtriExtractedValues(values);
        setHtriDialogOpen(true);
      } catch (error: any) {
        setHtriParseError(error.message || 'Failed to parse HTRI file. Please try again.');
      } finally {
        setIsHTRIParsing(false);
      }
    }
    // Reset the input value
    setTimeout(() => {
      if (htriFileInputRef.current) {
        htriFileInputRef.current.value = '';
      }
    }, 100);
  }, []);

  const handleHTRIDialogClose = React.useCallback(() => {
    setHtriDialogOpen(false);
    setHtriExtractedValues(null);
    setHtriFilename(null);
  }, []);

  const handleHTRIDialogConfirm = React.useCallback((values: any) => {
    // Dispatch event to the special flooded evaporator component
    const event = new CustomEvent('htriImportConfirmed', { 
      detail: { 
        values, 
        filename: htriFilename 
      } 
    });
    window.dispatchEvent(event);
    setHtriDialogOpen(false);
    setHtriExtractedValues(null);
    setHtriFilename(null);
  }, [htriFilename]);

  const handleHTRIDragOver = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleHTRIDragLeave = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleHTRIDrop = React.useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.dat')) {
        // Create a synthetic change event
        const syntheticEvent = {
          target: { files: [file] }
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleHTRIFileChange(syntheticEvent);
      }
    }
  }, [handleHTRIFileChange]);

  // Check if user has access to any heat exchanger applications that support file upload
  const hasHeatExchangerAccess = () => {
    if (!appAccess?.accessible_apps) return false;
    
    const heatExchangerAppIds = [
      'special_flooded_evaporator',
      'special_condenser',
      'coaxial_evaporator',
      'coaxial_condenser'
    ];
    
    return appAccess.accessible_apps.some(app => 
      heatExchangerAppIds.includes(app.app_id)
    );
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  // Helper function to create consistent composite icons (same as LandingPage)
  const createCompositeIcon = (baseIcon: React.ReactElement, overlayIcon: React.ReactElement) => (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {baseIcon}
      <Box sx={{
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        zIndex: 2
      }}>
        {overlayIcon}
      </Box>
    </Box>
  );

  // Generate drawer icons dynamically from app access data
  const getDrawerIconComponent = (iconType: IconType) => {
    const iconProps = { 
      sx: { 
        fontSize: 24, 
        transform: 'scale(1.2)'
      } 
    };
    
    switch (iconType) {
      case 'cold_plate':
        return (
          <Box sx={{ position: 'relative' }}>
            <ColdPlateIcon {...iconProps} />
          </Box>
        );
      case 'shell_tube_evap':
        return (
          <Box sx={{ position: 'relative' }}>
            <ShellAndTubeIcon {...iconProps} />
            <AcUnitIcon sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: '1px'
            }} />
          </Box>
        );
      case 'shell_tube_dry_evap':
        return (
          <Box sx={{ position: 'relative' }}>
            <ShellAndTubeDryIcon {...iconProps} />
            <AcUnitIcon sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: '1px'
            }} />
          </Box>
        );
      case 'shell_tube_condenser':
        return (
          <Box sx={{ position: 'relative' }}>
            <ShellAndTubeIcon {...iconProps} />
            <WaterDropIcon sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: '1px'
            }} />
          </Box>
        );
      case 'coaxial_evap':
        return (
          <Box sx={{ position: 'relative' }}>
            <CoaxialIcon {...iconProps} />
            <AcUnitIcon sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: '1px'
            }} />
          </Box>
        );
      case 'coaxial_condenser':
        return (
          <Box sx={{ position: 'relative' }}>
            <CoaxialIcon {...iconProps} />
            <WaterDropIcon sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 12,
              backgroundColor: theme.palette.background.paper,
              borderRadius: '50%',
              padding: '1px'
            }} />
          </Box>
        );
      case 'shell_tube_special_evap':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <AcUnitIcon sx={{
            fontSize: 12,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '50%',
            padding: '1px'
          }} />
        );
      case 'shell_tube_special_condenser':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <WaterDropIcon sx={{
            fontSize: 12,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '50%',
            padding: '1px'
          }} />
        );
      case 'tube_comparison':
        return <TubeComparisonIcon {...iconProps} />;
      case 'expert_app':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <StarIcon sx={{
            fontSize: 12,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '50%',
            padding: '1px'
          }} />
        );
      case 'documents':
        return <DescriptionIcon />;
      case 'air_heatexchanger':
        return createCompositeIcon(
          <AirHeatExchangerIcon {...iconProps} />,
          <AirIcon sx={{
            fontSize: 12,
            backgroundColor: theme.palette.background.paper,
            borderRadius: '50%',
            padding: '1px'
          }} />
        );
      default:
        return <HomeIcon />;
    }
  };

  // Generate dynamic drawer items from app access (replaces hardcoded items)
  const getDynamicDrawerItems = (): DrawerItem[] => {
    if (!appAccess?.accessible_apps) {
      return [];
    }

    return appAccess.accessible_apps.map((app: AppAccess) => ({
      text: app.app_name,
      icon: getDrawerIconComponent(app.icon_type as IconType),
      path: app.app_path
    }));
  };

  // Combine static items (Home, Terms, Privacy) with dynamic app items
  const staticDrawerItems: DrawerItem[] = [
    { text: 'Home', icon: <HomeIcon />, path: '/' },
  ];

  const dynamicAppItems = getDynamicDrawerItems();
  
  const staticFooterItems: DrawerItem[] = [
    { text: 'Terms of Use', icon: <GavelIcon />, path: '/terms-of-use' },
    { text: 'Privacy Policy', icon: <PrivacyTipIcon />, path: '/privacy-policy' },
  ];

  const allDrawerItems: DrawerItem[] = [
    ...staticDrawerItems,
    ...dynamicAppItems,
    ...staticFooterItems
  ];

  // Dynamic items are already filtered by backend, static items don't need permission checks
  const visibleDrawerItems = allDrawerItems;

  const drawer = (
    <Box sx={{ width: 250 }} onClick={handleDrawerToggle}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL}/wieland-logo-RGB-thermalx.png`}
          alt="Wieland Logo"
          sx={{
            height: 32,
            width: 'auto',
            mr: 1
          }}
        />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'white' }}>
          ThermalX
        </Typography>
      </Box>
      <Divider />
      <List>
        {visibleDrawerItems.map((item) => {
          // Find the app data to check if it's advanced/custom
          const appData = appAccess?.accessible_apps.find(app => app.app_path === item.path);
          const isCustom = appData?.is_advanced || false;
          
          return (
            <Box key={item.text} sx={{ position: 'relative' }}>
              <ListItemButton
                component={RouterLink} 
                to={item.path}
                sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}
              >
                <ListItemIcon>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                    {isCustom && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.5rem',
                          color: theme.palette.warning.main,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          lineHeight: 1,
                          opacity: 0.8
                        }}
                      >
                        Custom
                      </Typography>
                    )}
                    {item.icon}
                  </Box>
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
              
              <Tooltip title="Open in new tab">
                <IconButton
                  component="a"
                  href={getFullUrl(item.path)}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                    zIndex: 1,
                  }}
                >
                  <OpenInNewIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
        <Divider />
        {hasHeatExchangerAccess() && (
          <>
            <ListItemButton
              onClick={() => {
                setDrawerOpen(false);
                handleUploadClick();
              }}
              sx={{ '&:hover': { backgroundColor: theme.palette.action.hover }, cursor: 'pointer' }}
            >
              <ListItemIcon>
                <UploadIcon />
              </ListItemIcon>
              <ListItemText primary="Upload ThermalS Files" secondary="Legacy Support" />
            </ListItemButton>
            <Divider />
          </>
        )}
        <ListItemButton
            onClick={handleLogout}
            sx={{ '&:hover': { backgroundColor: theme.palette.action.hover }, cursor: 'pointer' }}
        >
          <ListItemIcon>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItemButton>
      </List>
    </Box>
  );

  const getAvatarLetter = (currentUser: UserDisplayInfo | null): string => {
    if (!currentUser) return '?';
    if (currentUser.first_name) return currentUser.first_name[0].toUpperCase();
    if (currentUser.username) return currentUser.username[0].toUpperCase();
    return '?';
  }

  const getUserDisplayName = (currentUser: UserDisplayInfo | null): string => {
    if (!currentUser) return 'User';
    if (currentUser.first_name && currentUser.last_name) return `${currentUser.first_name} ${currentUser.last_name}`;
    if (currentUser.username) return currentUser.username;
    return 'User';
  }

  const getCurrentFormName = (path: string): string | undefined => {
    // Use dynamic lookup based on user's accessible apps instead of hardcoded paths
    const currentApp = appAccess?.accessible_apps.find(app => app.app_path === path);
    return currentApp?.app_name;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar 
        position="static" 
        elevation={0}
        onDragOver={handleHTRIDragOver}
        onDragLeave={handleHTRIDragLeave}
        onDrop={handleHTRIDrop}
        sx={{
          cursor: location.pathname.includes('/special-flooded-evaporator') ? 'pointer' : 'default'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <RouterLink to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/wieland-logo-RGB-thermalx.png`}
              alt="Wieland Logo"
              sx={{
                height: 32,
                width: 'auto',
                mr: 1,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                mt: 0.25
              }}
            />
          </RouterLink>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'white' }}>
            ThermalX {title && title !== "ThermalX - Heat Exchanger Design" && (
              <>
                <Box 
                  component="span" 
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: '0.9em',
                    mx: 1,
                    fontWeight: 300,
                    
                  }}
                >
                  |
                </Box>
                <Box
                  component="span"
                  sx={{ 
                    display: {xs: 'none', sm: 'inline'},
                    fontSize: '0.9em',
                    fontWeight: 300,
                    
                  }}
                >
                  {title}
                </Box>
              </>
            )}
          </Typography>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* HTRI Import Button - only show for special flooded evaporator */}
              {location.pathname.includes('/special-flooded-evaporator') && (
                <>
                  <input
                    type="file"
                    ref={htriFileInputRef}
                    onChange={handleHTRIFileChange}
                    accept=".dat"
                    style={{ display: 'none' }}
                  />
                  <Tooltip title="Import HTRI File (.dat)">
                    <Button
                      color="inherit"
                      startIcon={<CloudUpload />}
                      onClick={() => htriFileInputRef.current?.click()}
                      disabled={isHTRIParsing}
                      sx={{ mr: 1 }}
                    >
                      {isHTRIParsing ? 'Parsing...' : 'Import HTRI'}
                    </Button>
                  </Tooltip>
                  {htriParseError && (
                    <Typography variant="caption" color="error" sx={{ mr: 1 }}>
                      {htriParseError}
                    </Typography>
                  )}
                </>
              )}
              {user && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.secondary.main, mr: 1 }}>
                    {getAvatarLetter(user)}
                  </Avatar>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    {getUserDisplayName(user)}
                  </Typography>
                </Box>
              )}
              <Box sx={{ mr: 1 }}>
                <ThemeToggle />
              </Box>
              <Tooltip title="Settings">
                <IconButton 
                  color="inherit"
                  onClick={handleOpenSettings}
                  sx={{ mr: 1 }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              <Button 
                color="inherit"
                onClick={handleLogout}
                startIcon={<LogoutIcon />}
              >
                Logout
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, 
        }}
      >
        {drawer}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, py: 3, backgroundColor: theme.palette.background.paper }}>
        <Container maxWidth="xl">
          {!isAppView && (
            <Typography variant="h4" component="h1" gutterBottom>
              {title}
            </Typography>
          )}
          {children}
        </Container>
      </Box>

      {/* Hidden FileUploadButton for burger menu */}
      <Box ref={fileUploadButtonRef} sx={{ display: 'none' }}>
        <FileUploadButton />
      </Box>

      {/* Settings Dialog */}
      <SettingsDialog 
        open={settingsOpen}
        onClose={handleCloseSettings}
        currentFormValues={currentFormValues}
      />


      {/* Support Chat Box */}
      <SupportChatBox 
        currentFormValues={currentFormValues}
        currentResults={currentResults}
        currentFormName={currentFormName || (isAppView ? getCurrentFormName(location.pathname) : undefined)}
        userEmail={user?.email}
        userName={getUserDisplayName(user)}
      />

      {/* HTRI Import Dialog */}
      {htriDialogOpen && htriExtractedValues && (
        <React.Suspense fallback={<div>Loading...</div>}>
          {React.createElement(
            React.lazy(() => import('../special_flooded_evaporator/ui/HTRIImportDialog')),
            {
              open: htriDialogOpen,
              onClose: handleHTRIDialogClose,
              onConfirm: handleHTRIDialogConfirm,
              extractedValues: htriExtractedValues,
              filename: htriFilename || ''
            }
          )}
        </React.Suspense>
      )}
    </Box>
  );
};

export default Layout;
